import { useCallback, useEffect, useState } from "react";

import { themeVar as colors } from "../design-tokens";
import { AppShell } from "../ui/AppShell";
import { PageHead } from "../ui/PageHead";
import { blockStyle } from "../ui/Block";
import { useDocumentTitle, vbTitle } from "../ui/useDocumentTitle";
import {
  NOT_AVAILABLE_LINE, NOT_OFFERED_LINE, errorLine, formatWhen, getMe, getMyAudit, getMyUsage,
  getTerms, isNotOffered, requestAccess, stateWord, type MlsAuditRow, type MlsMe, type MlsTerms,
  type MlsUsageRow,
} from "./api";
import { fieldStyle, primaryButton, whenDisabled } from "./styles";

/**
 * `/mls-access` — the member's MLS access card (Release 1.1 Slice A: the NTREIS group).
 *
 * Words, not colors: the state is printed as a word, with the server's own sentence, the reason
 * for the last change and its date. This card only ever shows the caller's own state.
 *
 * WHO SEES WHAT
 *  - Outside the NTREIS group: exactly one sentence, "MLS: not available on your account." No form,
 *    no state, no history: an agent cannot add themselves.
 *  - A member a group manager has added (Approved): the activation form. The member confirms their
 *    own NTREIS member ID (an identifier, not a credential) and accepts the data-use terms; that act
 *    turns MLS on (design G-4). The terms text is the server's placeholder, labeled as a draft.
 *  - Activated / Paused / Revoked: the state, why, and the member's own usage counts.
 *
 * Flag off: every route 404s, so the card is one sentence and no form.
 */
type Load =
  | { kind: "loading" }
  | { kind: "off" }
  | { kind: "error"; message: string }
  | { kind: "outside" }
  | { kind: "ready"; me: MlsMe; audit: MlsAuditRow[]; usage: MlsUsageRow[] };

export function MlsAccessScreen() {
  useDocumentTitle(vbTitle("MLS access"));
  const [load, setLoad] = useState<Load>({ kind: "loading" });

  const refresh = useCallback(async () => {
    try {
      const me = await getMe();
      if (!me.member) { setLoad({ kind: "outside" }); return; }
      const [audit, usage] = await Promise.all([getMyAudit(), getMyUsage()]);
      setLoad({ kind: "ready", me, audit, usage });
    } catch (e) {
      setLoad(isNotOffered(e) ? { kind: "off" }
        : { kind: "error", message: errorLine(e, "We couldn't load your MLS access just now.") });
    }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);

  return (
    <AppShell step="mls" content="form">
      <div style={{ ...blockStyle("form"), padding: "8px 0 48px" }}>
        <PageHead title="MLS access"
                  sub="NTREIS data is licensed to you as a member, so MLS is turned on for you personally." />
        <section aria-label="MLS access" data-testid="mls-card"
                 style={{ border: `1px solid ${colors.border}`, borderRadius: 10, padding: 20 }}>
          {load.kind === "loading" && <p style={{ color: colors.textSecondary }}>Loading…</p>}
          {load.kind === "off" && <p>{NOT_OFFERED_LINE}</p>}
          {load.kind === "outside" && <p data-testid="mls-not-available">{NOT_AVAILABLE_LINE}</p>}
          {load.kind === "error" && <p role="status">{load.message}</p>}
          {load.kind === "ready" && (
            <Ready me={load.me} audit={load.audit} usage={load.usage} onChanged={refresh} />
          )}
        </section>
      </div>
    </AppShell>
  );
}

const USE_LABELS: [string, string][] = [
  ["mls_search", "MLS searches"], ["mls_object", "MLS photos fetched"],
  ["report_used_mls", "Reports that used MLS data"],
];

function Ready({ me, audit, usage, onChanged }: {
  me: MlsMe; audit: MlsAuditRow[]; usage: MlsUsageRow[]; onChanged: () => void;
}) {
  const last = audit.length ? audit[audit.length - 1] : null;
  const changed = formatWhen(last?.at ?? me.updated_at);
  const canActivate = me.state === "Approved" || me.state === "Eligible";
  return (
    <>
      <p style={{ margin: 0, fontSize: 13, color: colors.textSecondary }}>Your status</p>
      <p data-testid="mls-state" style={{ margin: "2px 0 8px", fontSize: 22, fontWeight: 600 }}>
        {stateWord(me.state)}
      </p>
      <p data-testid="mls-explanation" style={{ margin: "0 0 8px", lineHeight: 1.55 }}>{me.explanation}</p>
      {me.state === "Activated" && !me.gate.ok && (
        <p data-testid="mls-gate" style={{ margin: "0 0 8px", lineHeight: 1.55 }}>{me.gate.reason}</p>
      )}
      {me.state !== null && (
        <dl style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>
          {me.mls_member_id && (<div><dt style={{ display: "inline", color: colors.textSecondary }}>NTREIS member ID: </dt>
            <dd style={{ display: "inline", margin: 0 }}>{me.mls_member_id}</dd></div>)}
          <div><dt style={{ display: "inline", color: colors.textSecondary }}>Last changed: </dt>
            <dd data-testid="mls-changed" style={{ display: "inline", margin: 0 }}>
              {changed ?? "date not recorded"}</dd></div>
          <div><dt style={{ display: "inline", color: colors.textSecondary }}>Reason: </dt>
            <dd data-testid="mls-reason" style={{ display: "inline", margin: 0 }}>
              {last?.reason ?? "no reason recorded"}</dd></div>
        </dl>
      )}
      {canActivate && <ActivateForm onDone={onChanged} />}
      {(me.state === "Paused" || me.state === "Revoked") && (
        <p data-testid="mls-no-form" style={{ margin: "12px 0 0", fontSize: 14, lineHeight: 1.55 }}>
          {me.state === "Paused"
            ? "Nothing is needed from you. An NTREIS group manager can resume it."
            : "MLS can't be turned on while access is revoked. An NTREIS group manager can reopen it."}
        </p>
      )}
      <div data-testid="mls-usage" style={{ marginTop: 16, fontSize: 14, lineHeight: 1.6 }}>
        <p style={{ margin: 0, fontSize: 13, color: colors.textSecondary }}>Your MLS use (all time)</p>
        {USE_LABELS.map(([ev, label]) => (
          <div key={ev}>{label}: {usage.filter((u) => u.event === ev).length}</div>
        ))}
      </div>
      {audit.length > 1 && (
        <details style={{ marginTop: 16 }}>
          <summary style={{ cursor: "pointer" }}>History</summary>
          <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 14, lineHeight: 1.6 }}>
            {audit.map((a, i) => (
              <li key={i}>{formatWhen(a.at)}: {a.old_state ?? "none"} to {a.new_state}
                {a.reason ? ` — ${a.reason}` : ""}</li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
}

function ActivateForm({ onDone }: { onDone: () => void }) {
  const [terms, setTerms] = useState<MlsTerms | null>(null);
  const [termsErr, setTermsErr] = useState(false);
  const [member, setMember] = useState("");
  const [accept, setAccept] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getTerms().then(setTerms).catch(() => setTermsErr(true));
  }, []);

  async function submit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!terms || !terms.can_accept) return;
    if (!member.trim()) { setError("Enter your NTREIS member ID to turn MLS on."); return; }
    if (!accept) { setError("Read and accept the data-use terms to turn MLS on."); return; }
    setBusy(true); setError(null);
    try {
      await requestAccess({ mls_member_id: member.trim(), accept_terms: true, terms_version: terms.version });
      onDone();
    } catch (e) {
      setError(errorLine(e, "We couldn't turn MLS on just now."));
    } finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} aria-label="Turn on MLS" style={{ marginTop: 16 }}>
      <label style={{ display: "block", fontSize: 14, fontWeight: 600 }} htmlFor="mls-member-id">
        Your NTREIS member ID
      </label>
      <input id="mls-member-id" value={member} maxLength={64} autoComplete="off"
             onChange={(e) => setMember(e.target.value)}
             style={{ ...fieldStyle, maxWidth: 320 }} />
      <p style={{ margin: "4px 0 12px", fontSize: 13, color: colors.textSecondary }}>
        This is your own member ID, not a password. ValuBrief never asks for MLS credentials.
      </p>
      <div data-testid="mls-terms" style={{ background: colors.bgApp,
           border: `1px solid ${colors.border}`, borderRadius: 8, padding: 12, fontSize: 13, lineHeight: 1.55 }}>
        <strong>{terms?.placeholder && !terms.attested_notice ? "Data-use terms (placeholder pending counsel)" : "Data-use terms"}</strong>
        {terms?.attested_notice ? <p data-testid="mls-terms-attested" style={{ margin: "6px 0 0" }}>{terms.attested_notice}</p>
          : terms ? <p style={{ margin: "6px 0 0" }}>{terms.text}</p>
          : <p style={{ margin: "6px 0 0" }}>{termsErr ? "The terms could not be loaded just now, so MLS can't be turned on yet." : "Loading the terms…"}</p>}
      </div>
      <label style={{ display: "flex", gap: 8, margin: "12px 0", fontSize: 14 }}>
        <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} />
        I have read and accept the data-use terms above.
      </label>
      {error && <p role="alert" data-testid="mls-form-error" style={{ margin: "0 0 8px", fontSize: 14 }}>{error}</p>}
      <button className="vb-btn-primary" type="submit" disabled={busy || !terms || !terms.can_accept}
              style={{ ...primaryButton, ...whenDisabled(busy || !terms || !terms.can_accept) }}>Turn on MLS</button>
      {!terms && (
        <p style={{ margin: "6px 0 0", fontSize: 13, color: colors.textSecondary }}>
          The button is available once the terms have loaded.
        </p>
      )}
      {terms && !terms.can_accept && (
        <p data-testid="mls-terms-pending" style={{ margin: "6px 0 0", fontSize: 13, color: colors.textSecondary }}>
          {terms.reason ?? "MLS cannot be turned on until the final data-use terms are in place."}
        </p>
      )}
    </form>
  );
}
